import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { getProgramByListId as getProgramFromDb, saveProgram as saveProgramToDb, publishProgram as publishProgramToDb, deleteProgram as deleteProgramFromDb, getWorkoutTemplates, getWarmupTemplates, getFinisherTemplates, getDrillTemplates, getExercises, getEquipmentTiers } from '../../utils/adminData';
import { getPersonasConfig, syncProgramToPersonasConfig } from '../../utils/database';

import AdminWorkoutEditorModal, { WORKOUT_PROGRAM_OPTIONS } from '../../components/AdminWorkoutEditorModal';
import '../../styles/Admin.css';
import '../../styles/Dashboard.css';
import '../../styles/Calendar.css';

const CMS_SECTIONS = [
  { id: 'palette', label: '1. Program Description', short: 'Description' },
  { id: 'macrocycles', label: '2. Macrocycles', short: 'Macro' },
  { id: 'mesocycles', label: '3. Mesocycles', short: 'Meso' },
  { id: 'build', label: '4. Build plan', short: 'Build' },
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MACRO_ACTIVE_FIELDS = new Set([
  'name', 'id', 'macrocycle_id', 'phase', 'order',
  'duration_weeks', 'weeks', 'start_month', 'end_month', 'months',
  'startDate', 'endDate',
  'weekly_activity_mix', 'mesocycles',
]);

function pickActiveFields(obj, allowedSet) {
  if (!obj) return obj;
  const out = {};
  for (const key of Object.keys(obj)) {
    if (allowedSet.has(key)) out[key] = obj[key];
  }
  if (out.weekly_activity_mix) {
    out.weekly_activity_mix = { day_assignments: out.weekly_activity_mix.day_assignments || {} };
  }
  return out;
}

function cleanWeeklyActivityMix(mix) {
  if (!mix || !mix.day_assignments) return mix;
  const da = mix.day_assignments;
  const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const counts = {};
  const restDays = [];
  const schedParts = {};

  DAYS_ORDER.forEach((day) => {
    const acts = Array.isArray(da[day]) ? da[day] : (da[day] ? [da[day]] : []);
    if (acts.length === 0 || (acts.length === 1 && acts[0] === 'rest')) {
      restDays.push(day);
      return;
    }
    acts.forEach((a) => {
      counts[a] = (counts[a] || 0) + 1;
      if (!schedParts[a]) schedParts[a] = [];
      schedParts[a].push(day.charAt(0).toUpperCase() + day.slice(1));
    });
  });

  const typicalSchedule = Object.entries(schedParts)
    .map(([type, days]) => `${type.charAt(0).toUpperCase() + type.slice(1)}: ${days.join('/')}`)
    .join('; ');

  return {
    day_assignments: da,
    ...(counts.strength ? { strength_sessions: counts.strength } : {}),
    ...(counts.drills ? { drill_sessions: counts.drills } : {}),
    ...(counts.cardio ? { cardio_sessions: counts.cardio } : {}),
    ...(counts.practice ? { practice_days: counts.practice } : {}),
    ...(counts.game ? { game_days: counts.game } : {}),
    rest_days: restDays.length > 0 ? restDays : undefined,
    typical_schedule: typicalSchedule || undefined,
  };
}

function getMesoCoverage(macro) {
  const totalWeeks = macro.duration_weeks || 0;
  const mesos = macro.mesocycles || [];
  let coveredWeeks = 0;
  let maxWeek = 0;
  mesos.forEach((m) => {
    const range = (m.weeks || '').split('-').map(Number);
    if (range.length === 2 && !isNaN(range[0]) && !isNaN(range[1])) {
      coveredWeeks += range[1] - range[0] + 1;
      if (range[1] > maxWeek) maxWeek = range[1];
    }
  });
  return { totalWeeks, coveredWeeks, maxWeek, mesoCount: mesos.length, gap: totalWeeks - coveredWeeks };
}

function AdminProgramForm({ programId, isNew, isFullScreen }) {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [isClosing, setIsClosing] = useState(false);
  const overlayRef = React.useRef(null);
  const mesoOverlayRef = React.useRef(null);
  const [isMesoClosing, setIsMesoClosing] = useState(false);
  const [showMesoCalendar, setShowMesoCalendar] = useState(false);
  const [activeSection, setActiveSection] = useState('palette');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [goals, setGoals] = useState([]);
  const [durationMonths, setDurationMonths] = useState('');
  const [trainingDaysPerWeek, setTrainingDaysPerWeek] = useState('');
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState('');
  const [slug, setSlug] = useState('');
  const [published, setPublished] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadLoading, setLoadLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [contentLoadErrors, setContentLoadErrors] = useState([]);
  const [publishProgress, setPublishProgress] = useState('');
  const [publishHistory, setPublishHistory] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [macrocycles, setMacrocycles] = useState([]);
  const [mesocycles, setMesocycles] = useState([]);
  const [microcycleTemplates, setMicrocycleTemplates] = useState([]); // eslint-disable-line no-unused-vars -- loaded for upcoming meso calendar
  const [buildStartDate, setBuildStartDate] = useState('');
  const [buildDurationWeeks, setBuildDurationWeeks] = useState('52');
  const [generatedSchedule, setGeneratedSchedule] = useState(null);

  const [programScheduleType, setProgramScheduleType] = useState('seasonal');
  const [expandedMacrocycle, setExpandedMacrocycle] = useState(null);
  const [editingMacrocycle, setEditingMacrocycle] = useState(null);
  const [editingMesocycle, setEditingMesocycle] = useState(null);
  const [editingWorkout, setEditingWorkout] = useState(null);

  const [macroJsonText, setMacroJsonText] = useState('');
  const [macroJsonError, setMacroJsonError] = useState(null);
  const macroJsonFocusedRef = useRef(false);

  const [mesoJsonText, setMesoJsonText] = useState('');
  const [mesoJsonError, setMesoJsonError] = useState(null);
  const mesoJsonFocusedRef = useRef(false);
  const [workoutsList, setWorkoutsList] = useState([]);
  const [warmupsList, setWarmupsList] = useState([]);
  const [finishersList, setFinishersList] = useState([]);
  const [drillsList, setDrillsList] = useState([]);
  const [exercisesListForWorkout, setExercisesListForWorkout] = useState([]);
  const [equipmentTierIds, setEquipmentTierIds] = useState([]);
  const [equipmentTiersList, setEquipmentTiersList] = useState([]);
  const [catalogProgramId, setCatalogProgramId] = useState(null);

  // Load personas/programs config from Firestore
  useEffect(() => {
    getPersonasConfig().then((data) => {
      if (data?.personas) setPersonas(data.personas);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!programId || isNew) {
      setLoadLoading(false);
      setLoadError(null);
      setName('');
      setCategoryId('');
      setDescription('');
      setGoals([]);
      setEquipmentTierIds([]);
      setCatalogProgramId(null);
      setDurationMonths('');
      setTrainingDaysPerWeek('');
      setSessionDurationMinutes('');
      setSlug('');
      setPublished(false);
      setGeneratedSchedule(null);
      return;
    }
    let cancelled = false;
    setLoadLoading(true);
    setLoadError(null);
    setSaveError('');
    getProgramFromDb(programId)
      .then((p) => {
        if (cancelled) return;
        if (!p) {
          setLoadError('Program not found in the database. Create it in the CMS first.');
          setLoadLoading(false);
          return;
        }
        setName(p.name || p.displayName || '');
        setCategoryId(p.category_id || p.persona_id || '');
        setDescription(p.description || '');
        setGoals(Array.isArray(p.goals) ? [...p.goals] : []);
        setDurationMonths(p.duration_months != null ? String(p.duration_months) : (p.durationWeeks != null ? String(p.durationWeeks) : ''));
        setTrainingDaysPerWeek(p.training_days_per_week != null ? String(p.training_days_per_week) : (p.workoutsPerWeek != null ? String(p.workoutsPerWeek) : ''));
        setSessionDurationMinutes(p.session_duration_minutes != null ? String(p.session_duration_minutes) : '');
        setSlug(p.id || p.programId || programId);
        setPublished(p.published === true);
        if (Array.isArray(p.publishHistory)) setPublishHistory(p.publishHistory);
        setCatalogProgramId(p.programId || p.id || programId);
        setEquipmentTierIds(Array.isArray(p.equipment_tier_ids) ? [...p.equipment_tier_ids] : []);
        const normMacros = (Array.isArray(p.macrocycles) ? p.macrocycles : []).map((m) => ({
          ...m,
          id: m.id || m.macrocycle_id || m.macrocycleId,
        }));
        setMacrocycles(normMacros);

        const normMesos = (Array.isArray(p.mesocycles) ? p.mesocycles : []).map((m) => ({
          ...m,
          id: m.id || m.mesocycle_id || m.mesocycleId,
          macrocycle_id: m.macrocycle_id || m.macrocycleId,
        }));
        setMesocycles(normMesos);

        if (p.programScheduleType) {
          setProgramScheduleType(p.programScheduleType);
        } else {
          const hasMon = normMacros.some((m) => m.start_month != null || m.startMonth != null);
          setProgramScheduleType(hasMon ? 'seasonal' : 'day1');
        }
        setMicrocycleTemplates([]);
        setBuildStartDate(p.programStartDate || '');
        setBuildDurationWeeks(p.durationWeeks != null ? String(p.durationWeeks) : '52');
        setGeneratedSchedule(p.generatedSchedule && typeof p.generatedSchedule === 'object' ? p.generatedSchedule : null);
        setLoadLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Failed to load program from database. Check connection and permissions.');
        setLoadLoading(false);
      });
    return () => { cancelled = true; };
  }, [programId, isNew]);

  const refreshWorkouts = React.useCallback(() => {
    getWorkoutTemplates()
      .then((workouts) => setWorkoutsList(Array.isArray(workouts) ? workouts : []))
      .catch(() => setWorkoutsList([]));
  }, []);

  // Load workouts, warmups, finishers, drills, exercises for workout editor (Firestore only)
  useEffect(() => {
    if (!programId || isNew) return;
    let cancelled = false;
    const errs = [];
    Promise.all([
      getWorkoutTemplates().catch(() => { errs.push('Workouts'); return []; }),
      getWarmupTemplates().catch(() => { errs.push('Warmups'); return []; }),
      getFinisherTemplates().catch(() => { errs.push('Finishers'); return []; }),
      getDrillTemplates().catch(() => { errs.push('Drills'); return []; }),
      getExercises().catch(() => { errs.push('Exercises'); return []; }),
    ]).then(([workouts, warmups, finishers, drills, exercises]) => {
      if (cancelled) return;
      setWorkoutsList(Array.isArray(workouts) ? workouts : []);
      setWarmupsList(Array.isArray(warmups) ? warmups : []);
      setFinishersList(Array.isArray(finishers) ? finishers : []);
      setDrillsList(Array.isArray(drills) ? drills : []);
      setExercisesListForWorkout(Array.isArray(exercises) ? exercises : []);
      setContentLoadErrors((prev) => [...prev.filter((x) => !['Workouts', 'Warmups', 'Finishers', 'Drills', 'Exercises'].some((k) => x.includes(k))), ...errs.map((e) => `${e} failed to load`)]);

    });
    return () => { cancelled = true; };
  }, [programId, isNew]);

  // Load equipment tiers on mount (for palette section in both new and existing programs)
  useEffect(() => {
    let cancelled = false;
    getEquipmentTiers()
      .then((tiers) => {
        if (cancelled) return;
        setEquipmentTiersList(Array.isArray(tiers) ? tiers : []);
        setContentLoadErrors((prev) => prev.filter((x) => x !== 'Equipment tiers failed to load'));
      })
      .catch(() => {
        if (cancelled) return;
        setEquipmentTiersList([]);
        setContentLoadErrors((prev) => (prev.includes('Equipment tiers failed to load') ? prev : [...prev, 'Equipment tiers failed to load']));
      });
    return () => { cancelled = true; };
  }, []);

  const goalsTextareaRef = React.useRef(null);
  const macroJsonRef = React.useRef(null);
  const mesoJsonRef = React.useRef(null);

  useEffect(() => {
    const ta = goalsTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
  }, [goals]);

  useEffect(() => {
    if (editingMacrocycle && !macroJsonFocusedRef.current) {
      setMacroJsonText(JSON.stringify(pickActiveFields(editingMacrocycle, MACRO_ACTIVE_FIELDS), null, 2));
      setMacroJsonError(null);
    }
    const ta = macroJsonRef.current;
    if (!ta) return;
    ta.style.height = '0';
    ta.style.height = ta.scrollHeight + 'px';
  }, [editingMacrocycle]);

  useEffect(() => {
    if (editingMesocycle && !mesoJsonFocusedRef.current) {
      setMesoJsonText(JSON.stringify(editingMesocycle, null, 2));
      setMesoJsonError(null);
    }
    const ta = mesoJsonRef.current;
    if (!ta) return;
    ta.style.height = '0';
    ta.style.height = ta.scrollHeight + 'px';
  }, [editingMesocycle]);

  const handleGoalsChange = (e) => {
    const text = e.target.value || '';
    setGoals(text ? text.split('\n').map((s) => s.trim()).filter(Boolean) : []);
    // Auto-grow textarea to fit content, no scroll
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
  };

  const persistToDb = async (overrides = {}) => {
    // When editing, programId from URL is authoritative; when adding new, slug is user-entered
    const id = !isNew && programId ? programId : (slug || programId);
    if (!id || isNew) return;
    const payload = {
      name: name || undefined,
      displayName: name || undefined,
      description: description || undefined,
      category_id: categoryId || undefined,
      goals: goals.length ? goals : undefined,
      equipment_tier_ids: equipmentTierIds.length ? equipmentTierIds : undefined,
      duration_months: durationMonths ? (Number(durationMonths) || durationMonths) : undefined,
      training_days_per_week: trainingDaysPerWeek || undefined,
      session_duration_minutes: sessionDurationMinutes ? (Number(sessionDurationMinutes) || sessionDurationMinutes) : undefined,
      published,
      programScheduleType,
      ...(macrocycles?.length ? { macrocycles } : {}),
      mesocycles: mesocycles || [],
      ...overrides,
    };
    await saveProgramToDb(id, payload);
  };

  const handleSaveAndContinue = async (nextSection) => {
    const id = slug || programId;
    if (id && !isNew) {
      setSaving(true);
      setSaveError('');
      try {
        await persistToDb();
        setSaveMessage('Saved.');
        setTimeout(() => setSaveMessage(''), 2000);
      } catch (err) {
        setSaveError(err?.message || 'Save failed');
        return;
      } finally {
        setSaving(false);
      }
    }
    setActiveSection(nextSection);
  };

  const handlePublish = async () => {
    const id = slug || programId;
    if (!id || isNew) {
      setSaveError('Publish is for existing programs.');
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveMessage('');
    setPublishProgress('Preparing payload…');
    try {
      const start = buildStartDate || new Date().toISOString().slice(0, 10);
      const weeks = Math.max(1, Math.min(52, parseInt(buildDurationWeeks, 10) || 52));
      const payload = {
        name: name || undefined,
        displayName: name || undefined,
        description: description || undefined,
        category_id: categoryId || undefined,
        goals: goals.length ? goals : undefined,
        equipment_tier_ids: equipmentTierIds.length ? equipmentTierIds : undefined,
        duration_months: durationMonths ? (Number(durationMonths) || durationMonths) : undefined,
        training_days_per_week: trainingDaysPerWeek || undefined,
        session_duration_minutes: sessionDurationMinutes ? (Number(sessionDurationMinutes) || sessionDurationMinutes) : undefined,
        published: true,
        programStartDate: start,
        durationWeeks: weeks,
        ...(macrocycles && macrocycles.length > 0 ? { macrocycles } : {}),
        ...(mesocycles && mesocycles.length > 0 ? { mesocycles } : {}),
        ...(generatedSchedule && Object.keys(generatedSchedule).length > 0 ? { generatedSchedule } : {}),
      };
      setPublishProgress('Writing to database…');
      const { templateVersion, publishHistory: dbHistory } = await publishProgramToDb(id, payload, catalogProgramId || id);

      // Sync persona assignment to config/personas
      if (categoryId) {
        setPublishProgress('Syncing personas config…');
        try {
          await syncProgramToPersonasConfig(catalogProgramId || id, {
            name: name || id,
            description: description || '',
            categoryId,
            status: 'available',
          });
        } catch (syncErr) {
          console.warn('[AdminProgramForm] Personas sync warning:', syncErr);
        }
      }

      setPublishProgress('Complete — v' + templateVersion);
      setPublished(true);
      if (dbHistory) setPublishHistory(dbHistory);
      setSaveMessage('');
    } catch (err) {
      console.error('[AdminProgramForm] Publish failed:', err);
      setSaveError(err?.message || 'Publish failed');
      setPublishProgress('');
    } finally {
      setSaving(false);
    }
  };

  const mesocyclesByMacro = (macro) =>
    (mesocycles || [])
      .filter((m) => (m.macrocycle_id || m.macrocycleId) === (macro?.id || macro?.macrocycle_id || macro?.macrocycleId))
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  /**
   * Generate a 7-day weekly template from a macrocycle's weekly_activity_mix.
   * Uses counts + rest_days to distribute activities across the week.
   *
   * Placement rules:
   *  1. Rest days are fixed from rest_days array
   *  2. Games: if rest includes monday → club pattern (weekend games), else school pattern (Tue/Thu)
   *  3. Strength: spread evenly across remaining days (max spacing)
   *  4. Drills → cardio → practice fill remaining slots
   *  5. If practice_days >= 5, game days become practice_or_game (daily practice + games)
   */
  const generateWeekFromMix = (mix) => {
    if (!mix) return null;
    const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const LABELS = {
      strength: 'Strength', game: 'Game', practice: 'Practice', practice_or_game: 'Practice / Game',
      drills: 'Drills', cardio: 'Cardio', running: 'Running', swimming: 'Swimming', biking: 'Biking',
      rest: 'Rest', recovery: 'Recovery', yoga: 'Yoga', hiit: 'HIIT', walking: 'Walking',
    };
    const week = {};

    // If explicit day_assignments exist, use them directly
    if (mix.day_assignments && Object.keys(mix.day_assignments).filter((k) => {
      const v = mix.day_assignments[k];
      return Array.isArray(v) ? v.length > 0 : !!v;
    }).length > 0) {
      DAYS.forEach((d) => {
        const raw = mix.day_assignments[d];
        const acts = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        if (acts.length === 0) return;
        // Primary type: strength takes priority for workout assignment, else first
        const primary = acts.includes('strength') ? 'strength' : acts[0];
        const name = acts.map((a) => LABELS[a] || a).join(' + ');
        week[d] = { type: primary, name, activities: acts };
      });
      return week;
    }

    // Fallback: algorithmic distribution from counts
    const restSet = new Set(mix.rest_days || []);
    restSet.forEach((d) => { week[d] = { type: 'rest', name: 'Rest' }; });

    const taken = new Set([...restSet]);
    const availForStrength = DAYS.filter((d) => !taken.has(d));
    const strengthCount = mix.strength_sessions || 0;
    const strengthSlots = [];
    if (strengthCount > 0 && availForStrength.length > 0) {
      const n = Math.min(strengthCount, availForStrength.length);
      for (let i = 0; i < n; i++) {
        const idx = n === 1 ? 0 : Math.round(i * (availForStrength.length - 1) / (n - 1));
        strengthSlots.push(availForStrength[idx]);
      }
    }
    strengthSlots.forEach((d) => { week[d] = { type: 'strength', name: 'Strength' }; taken.add(d); });

    const remaining = DAYS.filter((d) => !taken.has(d));
    let ri = 0;
    for (let i = 0; i < (mix.drill_sessions || 0) && ri < remaining.length; i++, ri++) {
      week[remaining[ri]] = { type: 'drills', name: 'Drills' };
    }
    for (let i = 0; i < (mix.cardio_sessions || 0) && ri < remaining.length; i++, ri++) {
      week[remaining[ri]] = { type: 'running', name: 'Cardio' };
    }
    for (let i = 0; i < (mix.game_days || 0) && ri < remaining.length; i++, ri++) {
      week[remaining[ri]] = { type: 'game', name: 'Game' };
    }
    for (let i = 0; i < (mix.practice_days || 0) && ri < remaining.length; i++, ri++) {
      week[remaining[ri]] = { type: 'practice', name: 'Practice' };
    }

    return week;
  };

  const runCloseAnimation = (onComplete) => {
    setIsClosing(true);
    const el = overlayRef.current;
    if (!el) {
      onComplete?.();
      return;
    }
    const handler = () => {
      el.removeEventListener('animationend', handler);
      onComplete?.();
    };
    el.addEventListener('animationend', handler);
  };

  const closeMesoEditor = () => {
    setIsMesoClosing(true);
    setTimeout(() => {
      setEditingMesocycle(null);
      setIsMesoClosing(false);
    }, 260);
  };

  // Validate workout_templates against the contract in src/utils/microcycleGenerator.js
  // (getWorkoutTemplateId): a 6-slot [A, B, C, D, E, F] array where D/E/F are the
  // even-week alternates of A/B/C (rotation resolves slot index -> index + 3). A short
  // or gappy array is exactly what caused repeated/misaligned workouts, so surface it.
  const validateMesoSlots = (meso) => {
    const problems = [];
    const wt = Array.isArray(meso?.workout_templates) ? meso.workout_templates : [];
    const filled = wt.filter(Boolean);
    if (filled.length === 0) return problems; // empty meso: nothing assigned yet, don't nag

    if (wt.length !== 6) {
      problems.push(
        `workout_templates should have 6 slots (A–F); found ${wt.length}. Even-week rotation (A→D, B→E, C→F) needs all six.`
      );
    }
    const letters = ['A', 'B', 'C'];
    const primaryCount = Math.min(Math.max(meso?.strength_sessions_per_week || filled.length || 3, 1), 3);
    for (let i = 0; i < primaryCount; i++) {
      if (!wt[i]) {
        problems.push(`Slot ${letters[i]} (primary, index ${i}) is empty but is scheduled.`);
      }
      if (!wt[i + 3]) {
        problems.push(
          `Slot ${letters[i]} even-week alternate (index ${i + 3}) is empty — even weeks will repeat slot ${letters[i]} instead of rotating.`
        );
      }
    }
    return problems;
  };

  const handleSaveMesoAndExit = async () => {
    const savedMeso = { ...editingMesocycle };

    const slotProblems = validateMesoSlots(savedMeso);
    if (slotProblems.length > 0) {
      const proceed = window.confirm(
        "These workout slots may cause repeated or misaligned workouts:\n\n• " +
        slotProblems.join('\n• ') +
        '\n\nExpected: 6 slots [A, B, C, D, E, F], where D/E/F are the even-week alternates of A/B/C.\n\nSave anyway?'
      );
      if (!proceed) {
        setSaveError('Mesocycle not saved — fix the A–F workout slots (or confirm to override).');
        return;
      }
    }

    const updated = mesocycles.map((m) => (m.id === savedMeso.id ? savedMeso : m));
    setMesocycles(updated);

    // Sync stub fields back to parent macrocycle's nested mesocycles array
    const parentMacroId = savedMeso.macrocycle_id || savedMeso.macrocycleId;
    if (parentMacroId) {
      setMacrocycles((prev) => prev.map((macro) => {
        const mid = macro.id || macro.macrocycle_id || macro.macrocycleId;
        if (mid !== parentMacroId || !Array.isArray(macro.mesocycles)) return macro;
        return {
          ...macro,
          mesocycles: macro.mesocycles.map((stub) =>
            (stub.mesocycle_id === savedMeso.id || stub.mesocycle_id === savedMeso.mesocycle_id)
              ? { ...stub, name: savedMeso.name, weeks: savedMeso.weeks, focus: savedMeso.focus, order: savedMeso.order }
              : stub
          ),
        };
      }));
    }

    const id = slug || programId;
    if (id && !isNew) {
      setSaving(true);
      setSaveError('');
      try {
        const payload = {
          name: name || undefined,
          displayName: name || undefined,
          description: description || undefined,
          category_id: categoryId || undefined,
          goals: goals.length ? goals : undefined,
          equipment_tier_ids: equipmentTierIds.length ? equipmentTierIds : undefined,
          duration_months: durationMonths ? (Number(durationMonths) || durationMonths) : undefined,
          training_days_per_week: trainingDaysPerWeek || undefined,
          session_duration_minutes: sessionDurationMinutes || undefined,
          published,
          ...(macrocycles?.length ? { macrocycles } : {}),
          mesocycles: updated,
        };
        await saveProgramToDb(id, payload);
        setSaveMessage('Mesocycle saved.');
        setTimeout(() => setSaveMessage(''), 3000);
      } catch (err) {
        setSaveError(err?.message || 'Save failed');
        return;
      } finally {
        setSaving(false);
      }
    }
    closeMesoEditor();
  };

  const handleCancel = () => runCloseAnimation(() => navigate('/admin/programs'));

  const handleRetryLoad = () => {
    if (!programId || isNew) return;
    setLoadError(null);
    setLoadLoading(true);
    getProgramFromDb(programId)
      .then((p) => {
        if (!p) {
          setLoadError('Program not found in the database. Create it in the CMS first.');
          return;
        }
        setName(p.name || p.displayName || '');
        setCategoryId(p.category_id || p.persona_id || '');
        setDescription(p.description || '');
        setGoals(Array.isArray(p.goals) ? [...p.goals] : []);
        setDurationMonths(p.duration_months != null ? String(p.duration_months) : (p.durationWeeks != null ? String(p.durationWeeks) : ''));
        setTrainingDaysPerWeek(p.training_days_per_week != null ? String(p.training_days_per_week) : (p.workoutsPerWeek != null ? String(p.workoutsPerWeek) : ''));
        setSessionDurationMinutes(p.session_duration_minutes != null ? String(p.session_duration_minutes) : '');
        setSlug(p.id || p.programId || programId);
        setPublished(p.published === true);
        if (Array.isArray(p.publishHistory)) setPublishHistory(p.publishHistory);
        setCatalogProgramId(p.programId || p.id || programId);
        setEquipmentTierIds(Array.isArray(p.equipment_tier_ids) ? [...p.equipment_tier_ids] : []);
        const reloadMacros = (Array.isArray(p.macrocycles) ? p.macrocycles : []).map((m) => ({
          ...m,
          id: m.id || m.macrocycle_id || m.macrocycleId,
        }));
        setMacrocycles(reloadMacros);

        const reloadMesos = (Array.isArray(p.mesocycles) ? p.mesocycles : []).map((m) => ({
          ...m,
          id: m.id || m.mesocycle_id || m.mesocycleId,
          macrocycle_id: m.macrocycle_id || m.macrocycleId,
        }));
        setMesocycles(reloadMesos);

        if (p.programScheduleType) setProgramScheduleType(p.programScheduleType);
        setBuildStartDate(p.programStartDate || '');
        setBuildDurationWeeks(p.durationWeeks != null ? String(p.durationWeeks) : '52');
        setGeneratedSchedule(p.generatedSchedule && typeof p.generatedSchedule === 'object' ? p.generatedSchedule : null);
      })
      .catch((err) => setLoadError(err?.message || 'Failed to load program from database.'))
      .finally(() => setLoadLoading(false));
  };

  const formContent = (
    <div className={`admin-content ${isFullScreen ? 'admin-program-form--fullscreen' : ''}`}>
      <div className="admin-content-panel">
        <div className="admin-dashboard card admin-program-form admin-program-form--full">
          <div className="admin-dashboard-header admin-program-form-header">
            {isFullScreen ? (
              <div className="admin-program-form-header--fullscreen">
                <h2 className="admin-dashboard-title" style={{ margin: 0 }}>
                  {isNew ? 'Add program' : `Edit program${name ? `: ${name}` : ''}`}
                </h2>
                {!isNew && (
                  <span className="admin-program-publish-status admin-program-publish-status--readonly">
                    {published ? 'Published' : 'Draft'}
                  </span>
                )}
              </div>
            ) : (
              <>
                <Link to="/admin/programs" className="admin-program-back-link">
                  ← Back to Programs
                </Link>
                <div className="admin-program-form-title-row">
                  <div className="admin-program-form-title-and-publish">
                    <h2 className="admin-dashboard-title">
                      {isNew ? 'Add program' : `Edit program${name ? `: ${name}` : ''}`}
                    </h2>
                    {!isNew && (
                      <span className="admin-program-publish-status admin-program-publish-status--readonly">
                        {published ? 'Published' : 'Draft'}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          {loadLoading && !isNew && (
            <div className="admin-content-loading" role="status">
              <p className="text-secondary">Loading program from database…</p>
            </div>
          )}
          {loadError && !loadLoading && (
            <div className="admin-program-load-error" role="alert">
              <p className="admin-program-load-error-message" style={{ color: 'var(--color-error)', fontWeight: 600 }}>
                {loadError}
              </p>
              <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-3)' }}>
                <button type="button" className="btn btn--secondary" onClick={handleRetryLoad}>
                  Retry
                </button>
                <Link to="/admin/programs" className="btn btn--secondary">
                  ← Back to Programs
                </Link>
              </div>
            </div>
          )}
          {contentLoadErrors.length > 0 && !loadError && (
            <div className="admin-program-content-errors" role="alert">
              <p className="text-secondary text-sm" style={{ color: 'var(--color-warning, #d69e2e)', marginBottom: 'var(--space-2)' }}>
                Some content failed to load from database: {contentLoadErrors.join(', ')}
              </p>
            </div>
          )}
          {saveError && (
        <p className="admin-dashboard-sync-message text-secondary text-sm" style={{ color: 'var(--color-error)' }} role="alert">
          {saveError}
        </p>
          )}
          {saveMessage && (
        <p className="admin-dashboard-sync-message text-secondary text-sm" role="status">
          {saveMessage}
        </p>
          )}

          {!loadLoading && !loadError && (
        <>
          {!isNew && (
        <nav className="admin-program-cms-nav" aria-label="Program CMS sections">
          {CMS_SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`admin-program-cms-nav-btn ${activeSection === s.id ? 'admin-program-cms-nav-btn--active' : ''}`}
              onClick={() => {
                setExpandedMacrocycle(null);
                setEditingMacrocycle(null);
                setEditingMesocycle(null);
                setActiveSection(s.id);
              }}
              aria-current={activeSection === s.id ? 'step' : undefined}
            >
              <span className="admin-program-cms-nav-label-full">{s.label}</span>
              <span className="admin-program-cms-nav-label-short">{s.short}</span>
            </button>
          ))}
        </nav>
          )}

          {activeSection === 'palette' && (
        <section className="admin-program-cms-section" aria-labelledby="program-palette-heading">
          <h3 id="program-palette-heading" className="admin-program-cms-section-title">
            1. Program Description
          </h3>
          <p className="admin-program-cms-section-desc text-secondary text-sm">
            Name, description, goals, and basic program settings.
          </p>
          <form className="admin-form" onSubmit={(e) => e.preventDefault()}>
            {isNew && (
              <div className="input-float-wrapper">
                <input
                  id="program-id"
                  type="text"
                  className="input"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder=" "
                />
                <label className="input-float-label" htmlFor="program-id">ID (slug)</label>
              </div>
            )}
            <div className="admin-program-palette-subsection">
              <h4 className="admin-program-palette-subtitle">Name, description, goals</h4>
            </div>
            <div className="input-float-wrapper">
              <input
                id="program-name"
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder=" "
              />
              <label className="input-float-label" htmlFor="program-name">Name</label>
            </div>
            <div>
              <div className="input-float-wrapper">
                <select
                  id="program-category"
                  className="input"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">— Select category —</option>
                  {personas.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <label className="input-float-label" htmlFor="program-category">Category</label>
              </div>
              <span className="admin-form-hint text-muted text-sm">Same categories as signup (Functional Strength, Athlete, etc.)</span>
            </div>
            <div className="input-float-wrapper">
              <textarea
                id="program-desc"
                className="input"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder=" "
              />
              <label className="input-float-label" htmlFor="program-desc">Description</label>
            </div>
            <div className="input-float-wrapper">
              <textarea
                ref={goalsTextareaRef}
                id="program-goals"
                className="input admin-program-goals-input"
                rows={4}
                value={goals.join('\n')}
                onChange={handleGoalsChange}
                placeholder=" "
              />
              <label className="input-float-label" htmlFor="program-goals">Goals (one per line)</label>
            </div>
            <div className="admin-program-palette-subsection">
              <h4 className="admin-program-palette-subtitle">Equipment tiers</h4>
              <p className="admin-program-palette-hint text-muted text-sm">Which facility configurations this program is compatible with.</p>
              <div className="admin-equipment-chips admin-program-equipment-tiers">
                {(equipmentTiersList || []).map((tier) => (
                  <label key={tier.id} className="admin-equipment-chip">
                    <input
                      type="checkbox"
                      checked={equipmentTierIds.includes(tier.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEquipmentTierIds((prev) => [...prev, tier.id]);
                        } else {
                          setEquipmentTierIds((prev) => prev.filter((id) => id !== tier.id));
                        }
                      }}
                    />
                    <span>{tier.name || tier.id}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="admin-form-row">
              <div className="input-float-wrapper">
                <input
                  id="program-duration"
                  type="text"
                  className="input"
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(e.target.value)}
                  placeholder=" "
                />
                <label className="input-float-label" htmlFor="program-duration">Duration (months)</label>
              </div>
              <div className="input-float-wrapper">
                <input
                  id="program-days"
                  type="text"
                  className="input"
                  value={trainingDaysPerWeek}
                  onChange={(e) => setTrainingDaysPerWeek(e.target.value)}
                  placeholder=" "
                />
                <label className="input-float-label" htmlFor="program-days">Training days/week</label>
              </div>
              <div className="input-float-wrapper">
                <input
                  id="program-session"
                  type="text"
                  className="input"
                  value={sessionDurationMinutes}
                  onChange={(e) => setSessionDurationMinutes(e.target.value)}
                  placeholder=" "
                />
                <label className="input-float-label" htmlFor="program-session">Session (min)</label>
              </div>
            </div>
            {!isNew && (
              <div className="admin-program-palette-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  {!published && (
                    confirmDelete ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: '0.85rem' }}>
                        <span style={{ color: 'var(--color-error, #e53e3e)' }}>Delete this program?</span>
                        <button type="button" className="btn btn--ghost btn--sm" style={{ color: 'var(--color-error, #e53e3e)', fontWeight: 600 }} disabled={deleteInProgress}
                          onClick={async () => {
                            setDeleteInProgress(true);
                            try {
                              await deleteProgramFromDb(slug || programId);
                              navigate('/admin/programs');
                            } catch (err) {
                              setSaveError(err?.message || 'Delete failed');
                              setConfirmDelete(false);
                            } finally {
                              setDeleteInProgress(false);
                            }
                          }}
                        >{deleteInProgress ? 'Deleting…' : 'Yes, delete'}</button>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirmDelete(false)}>No</button>
                      </span>
                    ) : (
                      <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-error, #e53e3e)', cursor: 'pointer', fontSize: '0.85rem', padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }} onClick={() => setConfirmDelete(true)}>
                        Delete program
                      </button>
                    )
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  {isFullScreen ? (
                    <button type="button" className="btn btn--secondary" onClick={handleCancel}>
                      Cancel
                    </button>
                  ) : (
                    <Link to="/admin/programs" className="btn btn--secondary">
                      Cancel
                    </Link>
                  )}
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => handleSaveAndContinue('macrocycles')}
                  >
                    Save &amp; continue
                  </button>
                </div>
              </div>
            )}
          </form>
        </section>
          )}

          {activeSection === 'macrocycles' && (
        <section className="admin-program-cms-section" aria-labelledby="macrocycles-heading">
          <h3 id="macrocycles-heading" className="admin-program-cms-section-title">
            2. Macrocycles
          </h3>
          <p className="admin-program-cms-section-desc text-secondary text-sm">
            Phase blocks (e.g. Winter Foundation, Spring Build). Each macro contains mesocycles. Loaded from program doc (DB or local).
          </p>
          {/* Schedule type radio */}
          <div className="admin-schedule-type-toggle" style={{ marginBottom: 'var(--space-5)', padding: 'var(--space-4)', background: 'var(--color-bg-input)', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)' }}>
            <label className="admin-form-label" style={{ marginBottom: 'var(--space-2)', display: 'block' }}>Program schedule type</label>
            <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="programScheduleType"
                  value="seasonal"
                  checked={programScheduleType === 'seasonal'}
                  onChange={() => setProgramScheduleType('seasonal')}
                  style={{ marginTop: '3px' }}
                />
                <span>
                  <strong style={{ display: 'block', fontSize: '0.9rem' }}>Seasonal</strong>
                  <span className="text-secondary text-sm">Athletes join at the current point in the season based on calendar months.</span>
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="programScheduleType"
                  value="day1"
                  checked={programScheduleType === 'day1'}
                  onChange={() => {
                    setProgramScheduleType('day1');
                    setMacrocycles((prev) => prev.map((m) => {
                      const { start_month, end_month, startMonth, endMonth, months, ...rest } = m;
                      return rest;
                    }));
                  }}
                  style={{ marginTop: '3px' }}
                />
                <span>
                  <strong style={{ display: 'block', fontSize: '0.9rem' }}>Starts from Day 1</strong>
                  <span className="text-secondary text-sm">Athletes always start from the beginning when they join.</span>
                </span>
              </label>
            </div>
          </div>

          {macrocycles.length > 0 ? (
            <ul className="admin-program-macro-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {macrocycles.map((macro) => {
                const macroId = macro.id || macro.macrocycle_id || macro.macrocycleId;
                const isExpanded = expandedMacrocycle === macroId;
                const isEditing = editingMacrocycle && (editingMacrocycle.id || editingMacrocycle.macrocycle_id || editingMacrocycle.macrocycleId) === macroId;
                const displayMacro = isEditing ? editingMacrocycle : macro;
                return (
                  <li key={macroId} className="admin-program-macro-item" style={{ marginBottom: 'var(--space-3)' }}>
                    <div style={{ background: 'var(--color-bg-input)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                      <div
                        role="button"
                        tabIndex={0}
                        style={{ padding: 'var(--space-3) var(--space-4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '2.5rem' }}
                        onClick={() => {
                          if (isExpanded) {
                            setExpandedMacrocycle(null);
                            setEditingMacrocycle(null);
                          } else {
                            setExpandedMacrocycle(macroId);
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedMacrocycle(isExpanded ? null : macroId); } }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 1 }}>
                          <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>&#9654;</span>
                          <div className="admin-program-macro-info">
                            <strong className="admin-program-macro-name">{displayMacro.name || displayMacro.id}</strong>
                            <span className="admin-program-macro-meta text-muted text-sm" style={{ marginLeft: 'var(--space-2)' }}>
                              {displayMacro.phase || ''} · {(displayMacro.duration_weeks ?? displayMacro.weeks) || ''} weeks
                              {(displayMacro.mesocycles && displayMacro.mesocycles.length) ? ` · ${displayMacro.mesocycles.length} mesocycles` : ''}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch' }}>
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isEditing) {
                                setEditingMacrocycle(null);
                              } else {
                                setExpandedMacrocycle(macroId);
                                setEditingMacrocycle({ ...macro });
                              }
                            }}
                          >
                            {isEditing ? 'Cancel' : 'Edit'}
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                      <div style={{ padding: 'var(--space-4)', borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-input)' }}>
                        {isEditing ? (
                          <>
                            <div className="admin-form-row">
                              <div className="input-float-wrapper">
                                <input
                                  type="text"
                                  className="input"
                                  placeholder=" "
                                  value={displayMacro.name || ''}
                                  onChange={(e) => setEditingMacrocycle((m) => ({ ...m, name: e.target.value }))}
                                />
                                <label className="input-float-label">Name</label>
                              </div>
                              <div className="input-float-wrapper">
                                <input
                                  type="text"
                                  className="input"
                                  placeholder=" "
                                  value={displayMacro.id || displayMacro.macrocycle_id || displayMacro.macrocycleId || ''}
                                  readOnly
                                  style={{ opacity: 0.7, cursor: 'default' }}
                                />
                                <label className="input-float-label">ID (read-only)</label>
                              </div>
                            </div>
                            <div className="admin-form-row" style={{ marginTop: 'var(--space-3)' }}>
                              <div className="input-float-wrapper">
                                <input
                                  type="text"
                                  className="input"
                                  placeholder=" "
                                  value={displayMacro.phase || ''}
                                  onChange={(e) => setEditingMacrocycle((m) => ({ ...m, phase: e.target.value }))}
                                />
                                <label className="input-float-label">Phase</label>
                              </div>
                              <div className="input-float-wrapper">
                                <input
                                  type="number"
                                  min={1}
                                  max={52}
                                  className="input"
                                  placeholder=" "
                                  value={displayMacro.duration_weeks ?? displayMacro.weeks ?? ''}
                                  onChange={(e) => setEditingMacrocycle((m) => ({ ...m, duration_weeks: parseInt(e.target.value, 10) || undefined }))}
                                />
                                <label className="input-float-label">Duration (weeks)</label>
                              </div>
                              <div className="input-float-wrapper">
                                <input
                                  type="number"
                                  min={1}
                                  max={20}
                                  className="input"
                                  placeholder=" "
                                  value={displayMacro.order ?? ''}
                                  onChange={(e) => setEditingMacrocycle((m) => ({ ...m, order: parseInt(e.target.value, 10) || undefined }))}
                                />
                                <label className="input-float-label">Order</label>
                              </div>
                            </div>
                            {programScheduleType === 'seasonal' && (
                            <div className="admin-macro-months-row" style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                              <div className="input-float-wrapper" style={{ flex: 1 }}>
                                <select
                                  className="input"
                                  value={displayMacro.start_month ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value ? parseInt(e.target.value, 10) : null;
                                    setEditingMacrocycle((m) => {
                                      const updated = { ...m, start_month: v };
                                      if (v != null && updated.end_month != null) {
                                        updated.months = MONTH_LABELS[v - 1] + '–' + MONTH_LABELS[updated.end_month - 1];
                                      }
                                      return updated;
                                    });
                                  }}
                                >
                                  <option value="">—</option>
                                  {MONTH_LABELS.map((label, i) => (
                                    <option key={i} value={i + 1}>{label}</option>
                                  ))}
                                </select>
                                <label className="input-float-label">Start Month</label>
                              </div>
                              <span className="text-secondary" style={{ paddingTop: '6px' }}>to</span>
                              <div className="input-float-wrapper" style={{ flex: 1 }}>
                                <select
                                  className="input"
                                  value={displayMacro.end_month ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value ? parseInt(e.target.value, 10) : null;
                                    setEditingMacrocycle((m) => {
                                      const updated = { ...m, end_month: v };
                                      if (updated.start_month != null && v != null) {
                                        updated.months = MONTH_LABELS[updated.start_month - 1] + '–' + MONTH_LABELS[v - 1];
                                      }
                                      return updated;
                                    });
                                  }}
                                >
                                  <option value="">—</option>
                                  {MONTH_LABELS.map((label, i) => (
                                    <option key={i} value={i + 1}>{label}</option>
                                  ))}
                                </select>
                                <label className="input-float-label">End Month</label>
                              </div>
                            </div>
                            )}
                            <div className="admin-meso-section" style={{ marginTop: 'var(--space-6)' }}>
                              <label className="admin-form-label">Weekly activity mix</label>
                              <p className="text-secondary text-sm admin-meso-section-hint">Assign activities to each day. Multiple activities per day are supported.</p>
                              <div className="admin-meso-mix-day-grid">
                                <div className="admin-meso-mix-day-row">
                                  {[
                                    { key: 'sunday', short: 'Sun' }, { key: 'monday', short: 'Mon' }, { key: 'tuesday', short: 'Tue' },
                                    { key: 'wednesday', short: 'Wed' }, { key: 'thursday', short: 'Thu' }, { key: 'friday', short: 'Fri' }, { key: 'saturday', short: 'Sat' },
                                  ].map(({ key, short }) => {
                                    const da = (displayMacro.weekly_activity_mix || {}).day_assignments || {};
                                    const raw = da[key];
                                    const activities = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                                    const OPTS = [
                                      { value: 'biking', label: 'Biking' }, { value: 'cardio', label: 'Cardio' },
                                      { value: 'drills', label: 'Drills' }, { value: 'game', label: 'Game' },
                                      { value: 'hiit', label: 'HIIT' }, { value: 'practice', label: 'Practice' },
                                      { value: 'practice_or_game', label: 'Practice / Game' }, { value: 'recovery', label: 'Recovery' },
                                      { value: 'rest', label: 'Rest' }, { value: 'running', label: 'Running' },
                                      { value: 'strength', label: 'Strength' }, { value: 'swimming', label: 'Swimming' },
                                      { value: 'walking', label: 'Walking' }, { value: 'yoga', label: 'Yoga' },
                                    ];
                                    const updateDayActivities = (newList) => {
                                      setEditingMacrocycle((m) => ({
                                        ...m,
                                        weekly_activity_mix: {
                                          ...(m.weekly_activity_mix || {}),
                                          day_assignments: { ...((m.weekly_activity_mix || {}).day_assignments || {}), [key]: newList.length > 0 ? newList : undefined },
                                        },
                                      }));
                                    };
                                    return (
                                      <div key={key} className="admin-meso-mix-day-cell">
                                        <span className="admin-meso-mix-day-label">{short}</span>
                                        {activities.map((act, ai) => (
                                          <div key={ai} className="admin-meso-mix-day-activity">
                                            <span className="admin-meso-mix-day-activity-label">{OPTS.find((o) => o.value === act)?.label || act}</span>
                                            <button type="button" className="admin-meso-mix-day-remove"
                                              onClick={() => updateDayActivities(activities.filter((_, idx) => idx !== ai))}
                                            >&times;</button>
                                          </div>
                                        ))}
                                        <select className="input input--sm admin-meso-mix-day-select" value=""
                                          onChange={(e) => { if (e.target.value) updateDayActivities([...activities, e.target.value]); }}
                                        >
                                          <option value="">+ Add</option>
                                          {OPTS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                            {/* Mesocycle coverage indicator */}
                            {(() => {
                              const cov = getMesoCoverage(displayMacro);
                              const hasGap = cov.gap > 0;
                              const nextOrder = cov.mesoCount + 1;
                              const nextStart = cov.maxWeek + 1;
                              const nextEnd = Math.min(nextStart + 3, cov.totalWeeks); // default 4-week block
                              const macroId = displayMacro.id || displayMacro.macrocycle_id || displayMacro.macrocycleId || '';
                              return (
                                <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius)', background: hasGap ? 'rgba(229,62,62,0.08)' : 'rgba(56,161,105,0.08)', border: `1px solid ${hasGap ? 'var(--color-error, #e53e3e)' : 'var(--color-success, #38a169)'}` }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                                    <div>
                                      <strong style={{ fontSize: '0.85rem' }}>Mesocycle coverage</strong>
                                      <span className="text-secondary text-sm" style={{ marginLeft: 'var(--space-2)' }}>
                                        {cov.mesoCount} mesocycle{cov.mesoCount !== 1 ? 's' : ''} · {cov.coveredWeeks} of {cov.totalWeeks} weeks
                                      </span>
                                      {hasGap && (
                                        <span style={{ color: 'var(--color-error, #e53e3e)', fontSize: '0.8rem', marginLeft: 'var(--space-2)', fontWeight: 600 }}>
                                          — {cov.gap} week{cov.gap !== 1 ? 's' : ''} uncovered (weeks {cov.maxWeek + 1}–{cov.totalWeeks})
                                        </span>
                                      )}
                                    </div>
                                    {hasGap && nextStart <= cov.totalWeeks && (
                                      <button
                                        type="button"
                                        className="btn btn--primary btn--sm"
                                        onClick={() => {
                                          const newMesoId = `${macroId}_meso${nextOrder}`;
                                          const stub = {
                                            order: nextOrder,
                                            mesocycle_id: newMesoId,
                                            name: `Phase ${nextOrder}`,
                                            weeks: `${nextStart}-${nextEnd}`,
                                            focus: '',
                                          };
                                          setEditingMacrocycle((m) => ({
                                            ...m,
                                            mesocycles: [...(m.mesocycles || []), stub],
                                          }));
                                          setMesocycles((prev) => [
                                            ...prev,
                                            {
                                              id: newMesoId,
                                              mesocycle_id: newMesoId,
                                              macrocycle_id: macroId,
                                              name: stub.name,
                                              order: stub.order,
                                              weeks: stub.weeks,
                                              duration_weeks: nextEnd - nextStart + 1,
                                              focus: '',
                                              workout_templates: [],
                                              weekly_schedule: {},
                                              progression_rules: {},
                                            },
                                          ]);
                                        }}
                                      >
                                        + Add Mesocycle (weeks {nextStart}–{nextEnd})
                                      </button>
                                    )}
                                  </div>
                                  {/* Editable mesocycle list */}
                                  {cov.mesoCount > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                                      {(displayMacro.mesocycles || []).map((m, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                                          <span className="text-secondary" style={{ fontSize: '0.75rem', minWidth: '1.2rem', textAlign: 'right' }}>{m.order || i + 1}.</span>
                                          <input
                                            type="text"
                                            className="input input--sm"
                                            style={{ flex: '0 0 160px', fontSize: '0.8rem' }}
                                            placeholder="Mesocycle name"
                                            value={m.name || ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              setEditingMacrocycle((mc) => ({
                                                ...mc,
                                                mesocycles: (mc.mesocycles || []).map((s, si) => si === i ? { ...s, name: val } : s),
                                              }));
                                            }}
                                          />
                                          <span className="text-secondary" style={{ fontSize: '0.75rem' }}>wk</span>
                                          <input
                                            type="text"
                                            className="input input--sm"
                                            style={{ flex: '0 0 80px', fontSize: '0.8rem', textAlign: 'center' }}
                                            placeholder="13-16"
                                            value={m.weeks || ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              setEditingMacrocycle((mc) => ({
                                                ...mc,
                                                mesocycles: (mc.mesocycles || []).map((s, si) => si === i ? { ...s, weeks: val } : s),
                                              }));
                                            }}
                                          />
                                          <input
                                            type="text"
                                            className="input input--sm"
                                            style={{ flex: 1, fontSize: '0.8rem' }}
                                            placeholder="Focus"
                                            value={m.focus || ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              setEditingMacrocycle((mc) => ({
                                                ...mc,
                                                mesocycles: (mc.mesocycles || []).map((s, si) => si === i ? { ...s, focus: val } : s),
                                              }));
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="admin-meso-mix-day-remove"
                                            title="Remove mesocycle"
                                            onClick={() => {
                                              setEditingMacrocycle((mc) => ({
                                                ...mc,
                                                mesocycles: (mc.mesocycles || []).filter((_, si) => si !== i),
                                              }));
                                            }}
                                          >&times;</button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            <div style={{ marginTop: 'var(--space-4)' }}>
                              <div className="admin-json-label-row">
                                <label className="admin-form-label">Active fields (JSON)</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                  {macroJsonError && <span style={{ color: 'var(--color-error, #e53e3e)', fontSize: '0.75rem' }}>Invalid JSON</span>}
                                  {!macroJsonError && macroJsonFocusedRef.current && <span style={{ color: 'var(--color-success, #38a169)', fontSize: '0.75rem' }}>Valid</span>}
                                  <button
                                    type="button"
                                    className="admin-json-copy-btn"
                                    title="Copy to clipboard"
                                    onClick={(e) => {
                                      navigator.clipboard.writeText(macroJsonText);
                                      const btn = e.currentTarget;
                                      btn.textContent = '✓';
                                      setTimeout(() => { btn.textContent = '⧉'; }, 1500);
                                    }}
                                  >⧉</button>
                                </div>
                              </div>
                              <textarea
                                ref={(el) => {
                                  macroJsonRef.current = el;
                                  if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
                                }}
                                className="input"
                                style={{
                                  width: '100%', fontFamily: 'monospace', fontSize: '0.85rem',
                                  overflow: 'hidden', resize: 'none', boxSizing: 'border-box',
                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                  borderColor: macroJsonError ? 'var(--color-error, #e53e3e)' : undefined,
                                }}
                                value={macroJsonText}
                                onFocus={() => { macroJsonFocusedRef.current = true; }}
                                onBlur={() => {
                                  macroJsonFocusedRef.current = false;
                                  if (!macroJsonError && editingMacrocycle) {
                                    setMacroJsonText(JSON.stringify(pickActiveFields(editingMacrocycle, MACRO_ACTIVE_FIELDS), null, 2));
                                  }
                                }}
                                onChange={(e) => {
                                  const text = e.target.value;
                                  setMacroJsonText(text);
                                  try {
                                    const parsed = JSON.parse(text);
                                    setEditingMacrocycle((prev) => ({ ...prev, ...parsed }));
                                    setMacroJsonError(null);
                                  } catch (err) {
                                    setMacroJsonError(err.message);
                                  }
                                  const ta = e.target;
                                  ta.style.height = 'auto';
                                  ta.style.height = ta.scrollHeight + 'px';
                                }}
                              />
                              {macroJsonError && (
                                <p style={{ color: 'var(--color-error, #e53e3e)', fontSize: '0.75rem', margin: '4px 0 0' }}>{macroJsonError}</p>
                              )}
                            </div>
                            <div style={{ marginTop: 'var(--space-6)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)' }}>
                              <button
                                type="button"
                                className="btn btn--secondary"
                                onClick={() => setEditingMacrocycle(null)}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn--primary"
                                disabled={saving}
                                onClick={async () => {
                                  const cleaned = { ...displayMacro };
                                  if (cleaned.weekly_activity_mix) {
                                    cleaned.weekly_activity_mix = cleanWeeklyActivityMix(cleaned.weekly_activity_mix);
                                  }
                                  // Persist both start_month and startMonth for database.js compatibility
                                  const sm = cleaned.start_month ?? cleaned.startMonth;
                                  const em = cleaned.end_month ?? cleaned.endMonth;
                                  if (sm != null) { cleaned.start_month = sm; cleaned.startMonth = sm; }
                                  if (em != null) { cleaned.end_month = em; cleaned.endMonth = em; }
                                  const newMacrocycles = macrocycles.map((m) => {
                                    const out = (m.id || m.macrocycle_id || m.macrocycleId) === macroId ? cleaned : { ...m };
                                    const mSm = out.start_month ?? out.startMonth;
                                    const mEm = out.end_month ?? out.endMonth;
                                    if (mSm != null) { out.start_month = mSm; out.startMonth = mSm; }
                                    if (mEm != null) { out.end_month = mEm; out.endMonth = mEm; }
                                    return out;
                                  });

                                  // Sync mesocycle stubs → flat mesocycles list
                                  const stubs = (cleaned.mesocycles || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
                                  const computeDurationWeeks = (w) => {
                                    if (w == null || w === '') return 4;
                                    const r = String(w).split('-').map(Number);
                                    return r.length === 2 ? r[1] - r[0] + 1 : (Number(w) || 4);
                                  };
                                  const existing = new Map(mesocycles.map((m) => [m.id || m.mesocycle_id, m]));
                                  stubs.forEach((stub) => {
                                    const dur = computeDurationWeeks(stub.weeks);
                                    if (!existing.has(stub.mesocycle_id)) {
                                      existing.set(stub.mesocycle_id, {
                                        id: stub.mesocycle_id,
                                        mesocycle_id: stub.mesocycle_id,
                                        macrocycle_id: macroId,
                                        name: stub.name,
                                        order: stub.order,
                                        weeks: stub.weeks,
                                        duration_weeks: dur,
                                        focus: stub.focus || '',
                                        workout_templates: [],
                                        weekly_schedule: {},
                                        progression_rules: {},
                                      });
                                    } else {
                                      const full = existing.get(stub.mesocycle_id);
                                      existing.set(stub.mesocycle_id, {
                                        ...full,
                                        name: stub.name,
                                        order: stub.order,
                                        weeks: stub.weeks,
                                        duration_weeks: dur,
                                        focus: stub.focus || full.focus,
                                        macrocycle_id: macroId,
                                      });
                                    }
                                  });
                                  const newMesocycles = [...mesocycles.filter((m) => (m.macrocycle_id || m.macrocycleId) !== macroId), ...stubs.map((s) => existing.get(s.mesocycle_id)).filter(Boolean)];

                                  setMacrocycles(newMacrocycles);
                                  setMesocycles(newMesocycles);
                                  setEditingMacrocycle(null);

                                  const id = slug || programId;
                                  if (id && !isNew) {
                                    setSaving(true);
                                    setSaveError('');
                                    try {
                                      await persistToDb({ macrocycles: newMacrocycles, mesocycles: newMesocycles });
                                      setSaveMessage('Macrocycle saved.');
                                      setTimeout(() => setSaveMessage(''), 2000);
                                    } catch (err) {
                                      setSaveError(err?.message || 'Save failed');
                                    } finally {
                                      setSaving(false);
                                    }
                                  }
                                }}
                              >
                                {saving ? 'Saving…' : 'Save macrocycle'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                            <div style={{ marginBottom: 'var(--space-2)' }}><strong>Name:</strong> {displayMacro.name || displayMacro.id}</div>
                            <div style={{ marginBottom: 'var(--space-2)' }}><strong>Phase:</strong> {displayMacro.phase || '—'}</div>
                            <div style={{ marginBottom: 'var(--space-2)' }}><strong>Duration:</strong> {(displayMacro.duration_weeks ?? displayMacro.weeks) || '—'} weeks</div>
                            {programScheduleType === 'seasonal' && (
                              <div style={{ marginBottom: 'var(--space-2)' }}><strong>Months:</strong> {displayMacro.start_month && displayMacro.end_month ? `${MONTH_LABELS[displayMacro.start_month - 1]}–${MONTH_LABELS[displayMacro.end_month - 1]}` : displayMacro.months || '—'}</div>
                            )}
                            {displayMacro.description && <div style={{ marginTop: 'var(--space-3)' }}><strong>Description:</strong> {displayMacro.description}</div>}
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-secondary text-sm">
              No macrocycles yet.
            </p>
          )}

          {!editingMacrocycle && (
            <button
              type="button"
              className="btn btn--secondary"
              style={{ marginTop: 'var(--space-4)' }}
              onClick={() => {
                const newId = `macro_${Date.now().toString(36)}`;
                const newMacro = {
                  id: newId,
                  name: '',
                  phase: '',
                  order: macrocycles.length + 1,
                  duration_weeks: 4,
                  months: '',
                  start_month: null,
                  end_month: null,
                  description: '',
                  weekly_activity_mix: { day_assignments: {} },
                };
                setMacrocycles((prev) => [...prev, newMacro]);
                setExpandedMacrocycle(newId);
                setEditingMacrocycle({ ...newMacro });
              }}
            >
              + Add macrocycle
            </button>
          )}

          {!isNew && !editingMacrocycle && (
            <div className="admin-program-palette-actions">
                {isFullScreen ? (
                  <button type="button" className="btn btn--secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                ) : (
                  <Link to="/admin/programs" className="btn btn--secondary">
                    Cancel
                  </Link>
                )}
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => handleSaveAndContinue('mesocycles')}
              >
                Save &amp; continue
              </button>
            </div>
          )}
        </section>
          )}

          {activeSection === 'mesocycles' && (
        <section className="admin-program-cms-section" aria-labelledby="mesocycles-heading">
          <h3 id="mesocycles-heading" className="admin-program-cms-section-title">
            3. Mesocycles
          </h3>
          <p className="admin-program-cms-section-desc text-secondary text-sm">
            Training blocks with workouts (Day A/B/C), week_structure, and progression_rules. Calendar is driven by macrocycle day assignments.
          </p>
          {macrocycles.length > 0 && mesocycles.length > 0 ? (
            <div className="admin-program-meso-list">
              {macrocycles.map((macro) => {
                const mesos = mesocyclesByMacro(macro);
                if (mesos.length === 0) return null;
                return (
                  <div key={macro.id || macro.macrocycle_id || macro.macrocycleId} className="admin-program-meso-group">
                    <h4 className="admin-program-meso-group-title">{macro.name || macro.id}</h4>
                    <ul className="admin-program-meso-items">
                      {mesos.map((meso) => (
                        <li
                          key={meso.id}
                          className="admin-program-meso-item"
                          role="button"
                          tabIndex={0}
                          onClick={() => setEditingMesocycle({ ...meso })}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingMesocycle({ ...meso }); } }}
                        >
                          <div>
                            <strong className="admin-program-meso-name">{meso.name || meso.id}</strong>
                            <span className="admin-program-meso-meta text-muted text-sm">
                              {(meso.duration_weeks ?? meso.weeks) || ''} weeks
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingMesocycle({ ...meso });
                            }}
                          >
                            Edit
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-secondary text-sm">
              {macrocycles.length > 0 ? 'No mesocycles in this program. Add mesocycles to macrocycles.' : 'No macrocycles. Add a macrocycle.'}
            </p>
          )}

          {editingMesocycle && createPortal(
            <div
              ref={mesoOverlayRef}
              className={`admin-meso-editor-backdrop${isMesoClosing ? ' admin-meso-editor-backdrop--closing' : ''}`}
              onClick={closeMesoEditor}
            >
              <div className="admin-meso-editor-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="admin-meso-editor-header">
                  <h3 className="admin-meso-editor-title">Edit mesocycle: {editingMesocycle.name || editingMesocycle.id}</h3>
                  <button
                    type="button"
                    className={`btn btn--sm ${showMesoCalendar ? 'btn--primary' : 'btn--secondary'}`}
                    onClick={() => setShowMesoCalendar((v) => !v)}
                  >
                    {showMesoCalendar ? '← Back to form' : 'View calendar'}
                  </button>
                </div>
                <div className="admin-meso-editor-body">
              {showMesoCalendar ? (() => {
                const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const numWeeks = editingMesocycle.duration_weeks || 4;
                const weeklySchedule = editingMesocycle.weekly_schedule || {};
                const workoutPool = (editingMesocycle.workout_templates || []).filter(Boolean);
                const workoutsForProgram = (workoutsList || []).filter(
                  (w) => !w.program || w.program === programId || w.program === 'shared'
                );
                // Generate base template from parent macrocycle's weekly_activity_mix
                const mesoMacroRef = editingMesocycle.macrocycle_id || editingMesocycle.macrocycleId;
                const parentMacro = macrocycles.find((m) => (m.id || m.macrocycle_id || m.macrocycleId) === mesoMacroRef);
                if (!parentMacro && mesoMacroRef) {
                  console.warn('[Calendar] No parent macrocycle found for mesocycle', editingMesocycle.id, '→ macrocycle_id:', mesoMacroRef, 'Available:', macrocycles.map((m) => m.id || m.macrocycle_id || m.macrocycleId));
                }
                const mixSchedule = generateWeekFromMix(parentMacro?.weekly_activity_mix) || {};
                // Fallback chain: week override > macrocycle mix > flat schedule
                const getDay = (weekIdx, dayKey) => {
                  const weekKey = `week_${weekIdx + 1}`;
                  const weekOverrides = (weeklySchedule[weekKey] || {})[dayKey];
                  if (weekOverrides) return weekOverrides;
                  if (mixSchedule[dayKey]) return mixSchedule[dayKey];
                  if (weeklySchedule[dayKey]) return weeklySchedule[dayKey];
                  return {};
                };
                const updateDay = (weekIdx, dayKey, patch) => {
                  setEditingMesocycle((m) => {
                    const ws = { ...(m.weekly_schedule || {}) };
                    const weekKey = `week_${weekIdx + 1}`;
                    const weekObj = { ...(ws[weekKey] || {}) };
                    weekObj[dayKey] = { ...getDay(weekIdx, dayKey), ...patch };
                    ws[weekKey] = weekObj;
                    return { ...m, weekly_schedule: ws };
                  });
                };
                const getRotationWorkout = (weekIdx, strengthIdxInWeek) => {
                  if (workoutPool.length === 0) return null;
                  const sessionsPerWeek = editingMesocycle.strength_sessions_per_week || workoutPool.length || 3;
                  const globalIdx = (weekIdx * sessionsPerWeek) + strengthIdxInWeek;
                  return workoutPool[globalIdx % workoutPool.length];
                };
                return (
                  <div className="admin-meso-cal">
                    <div className="admin-meso-cal-toolbar">
                      <p className="text-secondary text-sm">
                        {numWeeks}-week schedule. {parentMacro?.weekly_activity_mix ? `Base from ${parentMacro.name || 'macrocycle'} mix.` : ''} Rotation pool: {workoutPool.length ? workoutPool.map((_, i) => String.fromCharCode(65 + i)).join(', ') : 'none'}.
                      </p>
                    </div>
                    {(() => {
                      const wim = editingMesocycle.weeks_in_macrocycle;
                      let calStartWeek = 1;
                      if (typeof wim === 'string' && wim.includes('-')) {
                        calStartWeek = parseInt(wim.split('-')[0], 10) || 1;
                      } else {
                        const ek = Object.keys(editingMesocycle.week_structure || {}).filter((k) => k.startsWith('week_')).map((k) => parseInt(k.replace('week_', ''), 10)).filter(Boolean);
                        if (ek.length > 0) calStartWeek = Math.min(...ek);
                      }
                      return Array.from({ length: numWeeks }, (_, weekIdx) => {
                        const absWeek = calStartWeek + weekIdx;
                        return { weekIdx, absWeek };
                      });
                    })().map(({ weekIdx, absWeek }) => {
                      let strengthCount = 0;
                      const ws = (editingMesocycle.week_structure || {})[`week_${absWeek}`] || {};
                      return (
                        <div key={weekIdx} className="admin-meso-cal-week">
                          <div className="admin-meso-cal-week-header">
                            <strong>Week {absWeek}</strong>
                            {ws.name && <span className="text-secondary text-sm"> — {ws.name}</span>}
                            {ws.intensity_modifier != null && <span className="text-secondary text-sm"> · {Math.round(ws.intensity_modifier * 100)}% int</span>}
                          </div>
                          <div className="dashboard-calendar-grid calendar-full-grid admin-meso-cal-grid">
                            {DAY_SHORT.map((d) => (
                              <div key={d} className="dashboard-calendar-weekday">{d}</div>
                            ))}
                            {DAYS.map((dayKey) => {
                              const day = getDay(weekIdx, dayKey);
                              const dayType = day.type || '';
                              const rawActivities = day.activities || (dayType ? [dayType] : []);
                              const dayActivities = [...rawActivities].sort((a, b) => {
                                const isStr = (v) => v === 'strength' || v === 'strength_optional';
                                if (isStr(a) && !isStr(b)) return 1;
                                if (!isStr(a) && isStr(b)) return -1;
                                return 0;
                              });
                              const hasStrength = dayActivities.includes('strength') || dayActivities.includes('strength_optional');
                              const isRest = dayActivities.length === 1 && (dayActivities[0] === 'rest' || dayActivities[0] === 'recovery');
                              let rotationWorkout = null;
                              if (hasStrength) {
                                rotationWorkout = day.workout_template_id || day.workout_template || getRotationWorkout(weekIdx, strengthCount);
                                strengthCount++;
                              }
                              const rotationLetter = rotationWorkout && workoutPool.length > 0 ? String.fromCharCode(65 + workoutPool.indexOf(rotationWorkout)) : '';
                              const ACT_LABELS = { strength: 'Strength', game: 'Game', practice: 'Practice', practice_or_game: 'Practice / Game', drills: 'Drills', cardio: 'Cardio', running: 'Running', swimming: 'Swimming', biking: 'Biking', rest: 'Rest', recovery: 'Recovery', yoga: 'Yoga', hiit: 'HIIT' };
                              return (
                                <div key={dayKey} className={`calendar-day-card loaded admin-meso-day-card ${hasStrength ? 'strength' : ''} ${isRest ? 'rest-day' : ''}`} data-type={dayType}>
                                  {hasStrength && rotationLetter && (
                                    <div className="admin-meso-day-badge">{rotationLetter}</div>
                                  )}
                                  <div className="calendar-day-activities-desktop">
                                    {dayActivities.map((act, ai) => (
                                      <span key={ai} className={`admin-meso-day-act-label ${act}`}>{ACT_LABELS[act] || act}</span>
                                    ))}
                                    {hasStrength && (
                                      <select className="admin-meso-day-workout-select" 
                                        value={day.workout_template_id || day.workout_template || rotationWorkout || ''}
                                        onChange={(e) => updateDay(weekIdx, dayKey, { workout_template_id: e.target.value || undefined })}
                                      >
                                        <option value="">Auto</option>
                                        {workoutsForProgram.map((w) => (
                                          <option key={w.id} value={w.id}>{w.name || w.id}</option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })() : (
              <>
              <div className="admin-form-row">
                <div className="input-float-wrapper">
                  <input
                    type="text"
                    className="input"
                    placeholder=" "
                    value={editingMesocycle.name || ''}
                    onChange={(e) => setEditingMesocycle((m) => ({ ...m, name: e.target.value }))}
                  />
                  <label className="input-float-label">Name</label>
                </div>
                <div className="input-float-wrapper">
                  <input
                    type="number"
                    min={1}
                    max={12}
                    className="input"
                    placeholder=" "
                    value={editingMesocycle.duration_weeks ?? 4}
                    onChange={(e) => setEditingMesocycle((m) => ({ ...m, duration_weeks: parseInt(e.target.value, 10) || 4 }))}
                  />
                  <label className="input-float-label">Duration (weeks)</label>
                </div>
              </div>
              <div className="admin-meso-section--sm">
                <label className="admin-form-label">Workout templates (rotation pool)</label>
                <p className="text-secondary text-sm admin-meso-section-hint">Assign up to 6 workouts (A–F) that rotate through your weekly strength sessions.</p>
                <div className="admin-form-row admin-meso-workout-slots">
                  {(() => { const assigned = (editingMesocycle.workout_templates || []).filter(Boolean).length; return ['A', 'B', 'C', 'D', 'E', 'F'].slice(0, Math.min(Math.max(assigned + 1, editingMesocycle.strength_sessions_per_week ?? 3), 6)); })().map((letter, i) => {
                    const workoutsForProgram = (workoutsList || []).filter(
                      (w) => !w.program || w.program === programId || w.program === 'shared'
                    );
                    const assigned = (editingMesocycle.workout_templates && editingMesocycle.workout_templates[i]) || '';
                    return (
                      <div key={letter} className="admin-meso-workout-slot">
                        <div className="input-float-wrapper">
                          <select
                            className="input input--sm"
                            value={assigned}
                            onChange={(e) => {
                              const arr = [...(editingMesocycle.workout_templates || ['', '', '', '', '', ''])];
                              arr[i] = e.target.value;
                              setEditingMesocycle((m) => ({ ...m, workout_templates: arr }));
                            }}
                          >
                            <option value="">— Select workout —</option>
                            {workoutsForProgram.map((w) => (
                              <option key={w.id} value={w.id}>{w.name || w.id}</option>
                            ))}
                          </select>
                          <label className="input-float-label">Day {letter}</label>
                        </div>
                        {assigned && (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            title="Remove workout"
                            onClick={() => {
                              const arr = [...(editingMesocycle.workout_templates || ['', '', '', '', '', ''])];
                              arr[i] = '';
                              setEditingMesocycle((m) => ({ ...m, workout_templates: arr }));
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="admin-meso-section">
                <label className="admin-form-label">Other weekly activities</label>
                <p className="text-secondary text-sm admin-meso-section-hint">Non-strength activities in this mesocycle's weekly plan.</p>
                {(editingMesocycle.other_activities || []).map((act, ai) => (
                  <div key={ai} className="admin-form-row admin-meso-activity-row">
                    <div className="input-float-wrapper admin-meso-activity-type">
                      <select
                        className="input input--sm"
                        value={act.type || ''}
                        onChange={(e) => {
                          const type = e.target.value;
                          const nameMap = { practice: 'Team Practice', games: 'Games', drills: 'Drills', running: 'Running', swimming: 'Swimming', biking: 'Biking', recovery: 'Recovery', yoga: 'Yoga', walking: 'Walking', hiit: 'HIIT' };
                          setEditingMesocycle((m) => {
                            const arr = [...(m.other_activities || [])];
                            arr[ai] = { ...arr[ai], type, name: nameMap[type] || type };
                            if (type !== 'drills') delete arr[ai].drill_template_id;
                            return { ...m, other_activities: arr };
                          });
                        }}
                      >
                        <option value="">— Type —</option>
                        <option value="practice">Practice</option>
                        <option value="games">Games</option>
                        <option value="drills">Drills</option>
                        <option value="running">Running</option>
                        <option value="swimming">Swimming</option>
                        <option value="biking">Biking</option>
                        <option value="recovery">Recovery</option>
                        <option value="yoga">Yoga</option>
                        <option value="walking">Walking</option>
                        <option value="hiit">HIIT</option>
                      </select>
                      <label className="input-float-label">Type</label>
                    </div>
                    <div className="input-float-wrapper admin-meso-activity-name">
                      <input
                        type="text"
                        className="input input--sm"
                        placeholder=" "
                        value={act.name || ''}
                        onChange={(e) => setEditingMesocycle((m) => {
                          const arr = [...(m.other_activities || [])];
                          arr[ai] = { ...arr[ai], name: e.target.value };
                          return { ...m, other_activities: arr };
                        })}
                      />
                      <label className="input-float-label">Name</label>
                    </div>
                    <div className="input-float-wrapper admin-meso-activity-freq">
                      <input
                        type="number"
                        min={1}
                        max={7}
                        className="input input--sm"
                        placeholder=" "
                        value={act.sessions_per_week ?? 1}
                        onChange={(e) => setEditingMesocycle((m) => {
                          const arr = [...(m.other_activities || [])];
                          arr[ai] = { ...arr[ai], sessions_per_week: parseInt(e.target.value, 10) || 1 };
                          return { ...m, other_activities: arr };
                        })}
                      />
                      <label className="input-float-label">/ week</label>
                    </div>
                    {act.type === 'drills' && (
                      <div className="input-float-wrapper admin-meso-activity-drill">
                        <select
                          className="input input--sm"
                          value={act.drill_template_id || ''}
                          onChange={(e) => setEditingMesocycle((m) => {
                            const arr = [...(m.other_activities || [])];
                            arr[ai] = { ...arr[ai], drill_template_id: e.target.value || undefined };
                            return { ...m, other_activities: arr };
                          })}
                        >
                          <option value="">— Select drill —</option>
                          {drillsList.map((d) => (
                            <option key={d.id} value={d.id}>{d.name || d.id}</option>
                          ))}
                        </select>
                        <label className="input-float-label">Drill template</label>
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm admin-meso-activity-remove"
                      title="Remove activity"
                      onClick={() => setEditingMesocycle((m) => ({
                        ...m,
                        other_activities: (m.other_activities || []).filter((_, j) => j !== ai),
                      }))}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn--secondary btn--sm admin-meso-add-activity"
                  onClick={() => setEditingMesocycle((m) => ({
                    ...m,
                    other_activities: [...(m.other_activities || []), { type: '', name: '', sessions_per_week: 1 }],
                  }))}
                >
                  + Add activity
                </button>
              </div>
              <div className="admin-meso-section">
                <label className="admin-form-label">Week structure</label>
                <p className="text-secondary text-sm">Per-week modifiers (intensity, volume, RIR) for progression.</p>
                <table className="admin-program-other-activities-table admin-meso-week-table">
                  <thead>
                    <tr>
                      <th>Week</th>
                      <th>Name</th>
                      <th>Intensity</th>
                      <th>Volume</th>
                      <th>RIR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const nWeeks = editingMesocycle.duration_weeks || 4;
                      const wim = editingMesocycle.weeks_in_macrocycle;
                      let startWeek = 1;
                      if (typeof wim === 'string' && wim.includes('-')) {
                        startWeek = parseInt(wim.split('-')[0], 10) || 1;
                      } else {
                        const existingKeys = Object.keys(editingMesocycle.week_structure || {}).filter((k) => k.startsWith('week_')).map((k) => parseInt(k.replace('week_', ''), 10)).filter(Boolean);
                        if (existingKeys.length > 0) startWeek = Math.min(...existingKeys);
                      }
                      return Array.from({ length: nWeeks }, (_, i) => startWeek + i);
                    })().map((n) => {
                      const key = `week_${n}`;
                      const w = (editingMesocycle.week_structure || {})[key] || {};
                      return (
                        <tr key={key}>
                          <td>{n}</td>
                          <td>
                            <input
                              type="text"
                              className="input input--sm"
                              value={w.name || ''}
                              onChange={(e) => setEditingMesocycle((m) => ({
                                ...m,
                                week_structure: { ...(m.week_structure || {}), [key]: { ...w, name: e.target.value },
                                },
                              }))}
                              placeholder="e.g. Deload"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step={0.05}
                              min={0}
                              max={1.2}
                              className="input input--sm admin-meso-week-input--intensity"
                              value={w.intensity_modifier ?? ''}
                              onChange={(e) => setEditingMesocycle((m) => ({
                                ...m,
                                week_structure: { ...(m.week_structure || {}), [key]: { ...w, intensity_modifier: parseFloat(e.target.value) || undefined },
                                },
                              }))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step={0.05}
                              min={0}
                              max={1.2}
                              className="input input--sm admin-meso-week-input--volume"
                              value={w.volume_modifier ?? ''}
                              onChange={(e) => setEditingMesocycle((m) => ({
                                ...m,
                                week_structure: { ...(m.week_structure || {}), [key]: { ...w, volume_modifier: parseFloat(e.target.value) || undefined },
                                },
                              }))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              max={10}
                              className="input input--sm admin-meso-week-input--rir"
                              value={w.rir ?? ''}
                              onChange={(e) => setEditingMesocycle((m) => ({
                                ...m,
                                week_structure: { ...(m.week_structure || {}), [key]: { ...w, rir: parseInt(e.target.value, 10) || undefined },
                                },
                              }))}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="admin-meso-section--sm">
                <div className="admin-json-label-row">
                  <div>
                    <label className="admin-form-label">Progression rules (summary)</label>
                    <p className="text-secondary text-sm admin-meso-section-hint--flush">Compound: linear / double progression. Accessory: rep range + increment.</p>
                  </div>
                  <button
                    type="button"
                    className="admin-json-copy-btn"
                    title="Copy to clipboard"
                    onClick={(e) => {
                      const text = typeof editingMesocycle.progression_rules === 'object' ? JSON.stringify(editingMesocycle.progression_rules, null, 2) : (editingMesocycle.progression_rules || '');
                      navigator.clipboard.writeText(text);
                      const btn = e.currentTarget;
                      btn.textContent = '✓';
                      setTimeout(() => { btn.textContent = '⧉'; }, 1500);
                    }}
                  >⧉</button>
                </div>
                <div className="input-float-wrapper admin-meso-json-textarea-wrapper">
                  <textarea
                    className="input admin-meso-json-textarea"
                    rows={1}
                    placeholder=" "
                    value={typeof editingMesocycle.progression_rules === 'object' ? JSON.stringify(editingMesocycle.progression_rules, null, 2) : (editingMesocycle.progression_rules || '')}
                    onChange={(e) => {
                      try {
                        const v = e.target.value.trim();
                        const parsed = v ? JSON.parse(v) : {};
                        setEditingMesocycle((m) => ({ ...m, progression_rules: parsed }));
                      } catch (_) {
                        // leave as-is while typing
                      }
                      const ta = e.target;
                      ta.style.height = 'auto';
                      ta.style.height = ta.scrollHeight + 'px';
                    }}
                    ref={(el) => {
                      if (el) {
                        el.style.height = 'auto';
                        el.style.height = el.scrollHeight + 'px';
                      }
                    }}
                  />
                  <label className="input-float-label">Progression rules (JSON)</label>
                </div>
              </div>
              <div className="admin-meso-section--sm">
                <div className="admin-json-label-row">
                  <label className="admin-form-label">Full data (JSON)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    {mesoJsonError && <span style={{ color: 'var(--color-error, #e53e3e)', fontSize: '0.75rem' }}>Invalid JSON</span>}
                    {!mesoJsonError && mesoJsonFocusedRef.current && <span style={{ color: 'var(--color-success, #38a169)', fontSize: '0.75rem' }}>Valid</span>}
                    <button
                      type="button"
                      className="admin-json-copy-btn"
                      title="Copy to clipboard"
                      onClick={(e) => {
                        navigator.clipboard.writeText(mesoJsonText);
                        const btn = e.currentTarget;
                        btn.textContent = '✓';
                        setTimeout(() => { btn.textContent = '⧉'; }, 1500);
                      }}
                    >⧉</button>
                  </div>
                </div>
                <textarea
                  ref={(el) => {
                    mesoJsonRef.current = el;
                    if (el) {
                      el.style.height = 'auto';
                      el.style.height = el.scrollHeight + 'px';
                    }
                  }}
                  className="input admin-meso-json-full"
                  style={{
                    borderColor: mesoJsonError ? 'var(--color-error, #e53e3e)' : undefined,
                  }}
                  value={mesoJsonText}
                  onFocus={() => { mesoJsonFocusedRef.current = true; }}
                  onBlur={() => {
                    mesoJsonFocusedRef.current = false;
                    if (!mesoJsonError && editingMesocycle) {
                      setMesoJsonText(JSON.stringify(editingMesocycle, null, 2));
                    }
                  }}
                  onChange={(e) => {
                    const text = e.target.value;
                    setMesoJsonText(text);
                    try {
                      const parsed = JSON.parse(text);
                      setEditingMesocycle(parsed);
                      setMesoJsonError(null);
                    } catch (err) {
                      setMesoJsonError(err.message);
                    }
                    const ta = e.target;
                    ta.style.height = 'auto';
                    ta.style.height = ta.scrollHeight + 'px';
                  }}
                />
                {mesoJsonError && (
                  <p style={{ color: 'var(--color-error, #e53e3e)', fontSize: '0.75rem', margin: '4px 0 0' }}>{mesoJsonError}</p>
                )}
              </div>
              </>)}
                </div>
                <div className="admin-meso-editor-footer">
                  <button type="button" className="btn btn--secondary" onClick={closeMesoEditor}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn--primary" onClick={handleSaveMesoAndExit} disabled={saving}>
                    {saving ? 'Saving…' : 'Save & exit'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {!isNew && !editingMesocycle && (
            <div className="admin-program-palette-actions">
                {isFullScreen ? (
                  <button type="button" className="btn btn--secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                ) : (
                  <Link to="/admin/programs" className="btn btn--secondary">
                    Cancel
                  </Link>
                )}
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => handleSaveAndContinue('build')}
              >
                Save &amp; continue
              </button>
            </div>
          )}
        </section>
          )}

          {activeSection === 'build' && (
        <section className="admin-program-cms-section" aria-labelledby="build-heading">
          <h3 id="build-heading" className="admin-program-cms-section-title">
            4. Build plan
          </h3>
          <p className="admin-program-cms-section-desc text-secondary text-sm">
            Publish pushes the current program hierarchy to users. Each publish bumps the version number, triggering users to reload their program.
          </p>

          {macrocycles.length > 0 && (
            <div className="admin-build-progression">
              <div className="admin-build-progression-track">
                {macrocycles.map((macro, mi) => {
                  const mesos = (mesocycles || []).filter((m) => (m.macrocycle_id || m.macrocycleId) === (macro.id || macro.macrocycle_id || macro.macrocycleId));
                  const totalWeeks = mesos.reduce((sum, m) => sum + (m.duration_weeks || 4), 0);
                  return (
                    <div key={mi} className="admin-build-macro-block">
                      <div className="admin-build-macro-header">
                        <strong>{macro.name || macro.id}</strong>
                        <span className="text-secondary text-sm">{totalWeeks} weeks</span>
                      </div>
                      <div className="admin-build-meso-track">
                        {mesos.map((meso, msi) => (
                          <div key={msi} className="admin-build-meso-segment" style={{ flex: meso.duration_weeks || 4 }}>
                            <div className="admin-build-meso-bar" />
                            <span className="admin-build-meso-label">{meso.name || meso.id}</span>
                            <span className="admin-build-meso-weeks">{meso.duration_weeks || 4}w</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!isNew && (
            <div className="admin-program-palette-actions">
              <div className="admin-publish-row">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handlePublish}
                  disabled={saving || publishProgress.startsWith('Complete')}
                >
                  {saving ? 'Publishing…' : publishProgress.startsWith('Complete') ? 'Published' : 'Publish'}
                </button>
                {isFullScreen ? (
                  <button type="button" className="btn btn--secondary" onClick={handleCancel}>
                    {published ? 'Close' : 'Cancel'}
                  </button>
                ) : (
                  <Link to="/admin/programs" className="btn btn--secondary">
                    {published ? 'Close' : 'Cancel'}
                  </Link>
                )}
              </div>
              {publishHistory.length > 0 && (
                <div className="admin-publish-history">
                  <label className="admin-form-label text-secondary text-sm">Publish history</label>
                  <table className="admin-publish-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Version</th>
                      </tr>
                    </thead>
                    <tbody>
                      {publishHistory.map((h, i) => {
                        const dt = h.publishedAt ? new Date(h.publishedAt) : null;
                        return (
                          <tr key={i} className={i === 0 ? 'admin-publish-history-latest' : ''}>
                            <td>{dt ? dt.toLocaleDateString() : h.date || '—'}</td>
                            <td>{dt ? dt.toLocaleTimeString() : h.time || '—'}</td>
                            <td><code>{h.version}</code></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
          )}

          {editingWorkout && (
        <AdminWorkoutEditorModal
          item={editingWorkout}
          isAddMode={false}
          warmupsList={warmupsList}
          finishersList={finishersList}
          exercisesList={exercisesListForWorkout}
          defaultProgramId={programId}
          programOptions={[
            ...WORKOUT_PROGRAM_OPTIONS,
            ...(programId && !WORKOUT_PROGRAM_OPTIONS.some((o) => o.value === programId)
              ? [{ value: programId, label: programId }]
              : []),
          ]}
          onSave={refreshWorkouts}
          onClose={() => setEditingWorkout(null)}
          onDelete={refreshWorkouts}
          showDelete={true}
        />
          )}
        </>
          )}
        </div>
      </div>
    </div>
  );

  if (isFullScreen) {
    return createPortal(
      <div
        ref={overlayRef}
        className={`admin-program-edit-overlay ${isClosing ? 'admin-program-edit-overlay--closing' : 'admin-program-edit-overlay--open'}`}
      >
        {formContent}
      </div>,
      document.body
    );
  }
  return formContent;
}

export default AdminProgramForm;
